import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Edit3, Trash2, Eye, EyeOff, Save, FileText } from "lucide-react";
import { API } from "./shared";

const BlogEditor = ({ post, onSave, onCancel, saving }) => {
  const [form, setForm] = useState({
    title: post?.title || "",
    slug: post?.slug || "",
    excerpt: post?.excerpt || "",
    content: post?.content || "",
    meta_description: post?.meta_description || "",
    featured_image: post?.featured_image || "",
    tags: (post?.tags || []).join(", "),
    author: post?.author || "Fitly India",
    published: post?.published ?? true
  });

  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const handleTitleChange = (val) => {
    setForm(f => ({ ...f, title: val, slug: post ? f.slug : autoSlug(val) }));
  };

  const handleSubmit = () => {
    if (!form.title || !form.slug || !form.content) return;
    onSave({
      ...form,
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean)
    });
  };

  return (
    <div className="bg-white rounded-2xl border border-emerald_deep/5 shadow-sm p-6" data-testid="blog-editor">
      <h3 className="font-serif text-xl text-emerald_deep mb-6">{post ? "Edit Article" : "New Article"}</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-emerald_deep mb-1">Title *</label>
          <input value={form.title} onChange={e => handleTitleChange(e.target.value)} data-testid="blog-title-input"
            className="w-full px-4 py-2.5 rounded-xl border border-emerald_deep/10 bg-bone/50 text-emerald_deep focus:outline-none focus:ring-2 focus:ring-accent/40" />
        </div>
        <div>
          <label className="block text-sm text-emerald_deep mb-1">Slug *</label>
          <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} data-testid="blog-slug-input"
            className="w-full px-4 py-2.5 rounded-xl border border-emerald_deep/10 bg-bone/50 text-emerald_deep focus:outline-none focus:ring-2 focus:ring-accent/40 font-mono text-sm" />
        </div>
        <div>
          <label className="block text-sm text-emerald_deep mb-1">Excerpt</label>
          <textarea value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} rows={2} data-testid="blog-excerpt-input"
            className="w-full px-4 py-2.5 rounded-xl border border-emerald_deep/10 bg-bone/50 text-emerald_deep focus:outline-none focus:ring-2 focus:ring-accent/40 text-sm" />
        </div>
        <div>
          <label className="block text-sm text-emerald_deep mb-1">Content (HTML) *</label>
          <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={12} data-testid="blog-content-input"
            className="w-full px-4 py-2.5 rounded-xl border border-emerald_deep/10 bg-bone/50 text-emerald_deep focus:outline-none focus:ring-2 focus:ring-accent/40 font-mono text-xs leading-relaxed" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-emerald_deep mb-1">Meta Description</label>
            <textarea value={form.meta_description} onChange={e => setForm(f => ({ ...f, meta_description: e.target.value }))} rows={2}
              className="w-full px-4 py-2.5 rounded-xl border border-emerald_deep/10 bg-bone/50 text-emerald_deep focus:outline-none focus:ring-2 focus:ring-accent/40 text-sm" />
          </div>
          <div>
            <label className="block text-sm text-emerald_deep mb-1">Featured Image URL</label>
            <input value={form.featured_image} onChange={e => setForm(f => ({ ...f, featured_image: e.target.value }))}
              className="w-full px-4 py-2.5 rounded-xl border border-emerald_deep/10 bg-bone/50 text-emerald_deep focus:outline-none focus:ring-2 focus:ring-accent/40 text-sm" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-emerald_deep mb-1">Tags (comma-separated)</label>
            <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
              className="w-full px-4 py-2.5 rounded-xl border border-emerald_deep/10 bg-bone/50 text-emerald_deep focus:outline-none focus:ring-2 focus:ring-accent/40 text-sm" />
          </div>
          <div>
            <label className="block text-sm text-emerald_deep mb-1">Author</label>
            <input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))}
              className="w-full px-4 py-2.5 rounded-xl border border-emerald_deep/10 bg-bone/50 text-emerald_deep focus:outline-none focus:ring-2 focus:ring-accent/40 text-sm" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))}
              className="w-4 h-4 rounded border-emerald_deep/20 text-accent focus:ring-accent" />
            <span className="text-sm text-emerald_deep">Published</span>
          </label>
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <button onClick={onCancel} className="px-6 py-2.5 rounded-xl border border-emerald_deep/10 text-text_muted text-sm hover:bg-bone transition-colors">Cancel</button>
        <button onClick={handleSubmit} disabled={saving || !form.title || !form.slug || !form.content} data-testid="blog-save-btn"
          className="px-6 py-2.5 rounded-xl bg-emerald_deep text-bone text-sm font-medium hover:bg-emerald_deep/90 transition-all disabled:opacity-50 flex items-center gap-2">
          <Save size={14} /> {saving ? "Saving..." : "Save Article"}
        </button>
      </div>
    </div>
  );
};

export default function BlogTab({ token, onRefresh }) {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/blog`, { headers: { Authorization: `Bearer ${token}` } });
      setPosts(res.data.posts || []);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error("Failed to fetch blog posts:", err);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/blog`, data, { headers: { Authorization: `Bearer ${token}` } });
      setEditing(null);
      fetchPosts();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const handleUpdate = async (data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/blog/${editing.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      setEditing(null);
      fetchPosts();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchPosts();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
    setDeleting(null);
  };

  if (editing) {
    return (
      <BlogEditor
        post={editing === "new" ? null : editing}
        onSave={editing === "new" ? handleCreate : handleUpdate}
        onCancel={() => setEditing(null)}
        saving={saving}
      />
    );
  }

  return (
    <div data-testid="blog-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-xl text-emerald_deep">Blog Articles ({posts.length})</h3>
        <button onClick={() => setEditing("new")} data-testid="new-blog-btn"
          className="flex items-center gap-2 px-5 py-2 bg-emerald_deep text-bone text-sm rounded-full font-medium hover:bg-emerald_deep/90 transition-all">
          <Plus size={14} /> New Article
        </button>
      </div>
      {posts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-emerald_deep/5 p-12 text-center text-text_muted">
          <FileText size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No blog articles yet. Create your first one!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <div key={post.id} className="bg-white rounded-2xl border border-emerald_deep/5 shadow-sm p-5 flex items-center gap-4" data-testid={`admin-blog-${post.slug}`}>
              {post.featured_image && (
                <img src={post.featured_image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0 hidden sm:block" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-serif text-base text-emerald_deep truncate">{post.title}</h4>
                  {post.published ? (
                    <span className="flex items-center gap-1 text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full"><Eye size={10} /> Live</span>
                  ) : (
                    <span className="flex items-center gap-1 text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full"><EyeOff size={10} /> Draft</span>
                  )}
                </div>
                <p className="text-xs text-text_muted truncate">/blog/{post.slug}</p>
                {post.tags?.length > 0 && (
                  <div className="flex gap-1 mt-1.5">
                    {post.tags.slice(0, 4).map(tag => (
                      <span key={tag} className="text-[9px] bg-bone text-text_muted px-2 py-0.5 rounded-full">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-lg hover:bg-bone text-text_muted hover:text-emerald_deep transition-colors" title="View">
                  <Eye size={16} />
                </a>
                <button onClick={() => setEditing(post)} data-testid={`edit-blog-${post.slug}`}
                  className="p-2 rounded-lg hover:bg-bone text-text_muted hover:text-emerald_deep transition-colors" title="Edit">
                  <Edit3 size={16} />
                </button>
                <button onClick={() => handleDelete(post.id)} disabled={deleting === post.id} data-testid={`delete-blog-${post.slug}`}
                  className="p-2 rounded-lg hover:bg-red-50 text-text_muted hover:text-red-500 transition-colors disabled:opacity-50" title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
